import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiLogOut, FiChevronDown, FiSettings, FiRepeat, FiChevronRight, FiCheck, FiUsers } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { updateAdminFocusTrack } from '../../config/api';
import { TRACK_NAMES } from '../../types/constants';
import { canUseEarlyGradeMove } from '../../policies/earlyGradeMovePolicy';

type TrackId = keyof typeof TRACK_NAMES;

interface TrackGroup {
  label: string;
  tracks: TrackId[];
}

const TRACK_GROUPS: TrackGroup[] = [
  {
    label: 'Pre-K',
    tracks: [
      'TRACK_PREK_NAMING',
      'TRACK_PREK_SUBITIZING',
      'TRACK_PREK_COUNTING',
      'TRACK_PREK_WHATCOMESNEXT',
    ],
  },
  {
    label: 'Kindergarten',
    tracks: [
      'TRACK_K_NAMING',
      'TRACK_K_SUBITIZING',
      'TRACK_K_COMPARING',
      'TRACK_K_PARTWHOLE',
      'TRACK_K_ADDITION_WITHIN_5',
      'TRACK_K_ACCURACY',
      'TRACK13',
    ],
  },
  {
    label: 'Grade 1',
    tracks: ['TRACK12'],
  },
  {
    label: 'Grade 2',
    tracks: ['TRACK9', 'TRACK10'],
  },
  {
    label: 'Grade 3',
    tracks: ['TRACK6', 'TRACK8', 'TRACK11'],
  },
  {
    label: 'Grade 4',
    tracks: ['TRACK7', 'TRACK5'],
  },
  {
    label: 'Grade 5',
    tracks: ['TRACK5_L2', 'TRACK6_L2', 'TRACK7_L2', 'TRACK8_L2'],
  },
];

const UserProfileDropdown: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showTrackPanel, setShowTrackPanel] = useState(false);
  const [isChangingTrack, setIsChangingTrack] = useState(false);
  const [trackError, setTrackError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside - only when open
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowTrackPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      // Call logout from AuthContext (which clears localStorage and sessionStorage)
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      // Still navigate to login on error
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate, isLoggingOut]);

  const currentFocusTrack = user?.focusTrack as TrackId | undefined;

  const handleTrackSelect = useCallback(async (trackId: TrackId) => {
    if (isChangingTrack || trackId === currentFocusTrack) return;
    try {
      setTrackError(false);
      setIsChangingTrack(true);
      await updateAdminFocusTrack(trackId);
      window.location.href = '/';
    } catch (_err) {
      setIsChangingTrack(false);
      setTrackError(true);
    }
  }, [isChangingTrack, currentFocusTrack]);

  // Memoized initials calculation
  const initials = useMemo(() => {
    if (!user?.email) return '?';

    if (user.name && typeof user.name === 'string') {
      const names = user.name.trim().split(/\s+/).filter(Boolean);
      if (names.length > 0) {
        return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
      }
    }

    return user.email[0]?.toUpperCase() || '?';
  }, [user?.name, user?.email]);

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoggingOut}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
          {initials}
        </div>
        <FiChevronDown className={`text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[9999] w-[280px]"
          >
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  {user.name && (
                    <p className="font-semibold text-gray-900 truncate">{user.name}</p>
                  )}
                  <p className="text-sm text-gray-600 truncate">{user.email}</p>
                </div>
              </div>
            </div>

            <div className="p-2">
              {user.role === 'admin' && (
                <>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setShowTrackPanel(false);
                      navigate('/admin');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200"
                  >
                    <FiSettings className="text-gray-500 shrink-0" />
                    <span>Admin Panel</span>
                  </button>

                  <button
                    onClick={() => setShowTrackPanel(!showTrackPanel)}
                    disabled={isChangingTrack}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChangingTrack ? (
                      <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin shrink-0" />
                    ) : (
                      <FiRepeat className="text-gray-500 shrink-0" />
                    )}
                    <span className="flex-1">{isChangingTrack ? 'Switching...' : 'Change focus track'}</span>
                    <FiChevronRight
                      className={`text-gray-400 transition-transform duration-200 ${showTrackPanel ? 'rotate-90' : ''}`}
                    />
                  </button>

                  <AnimatePresence>
                    {showTrackPanel && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-1 ml-2 max-h-72 overflow-y-auto border border-gray-100 rounded-md bg-gray-50">
                          {trackError && (
                            <p className="px-3 py-2 text-xs text-red-600 bg-red-50">Failed to switch track. Please try again.</p>
                          )}
                          {TRACK_GROUPS.map((group) => (
                            <div key={group.label}>
                              <p className="px-3 pt-2 pb-1 text-xs font-bold text-gray-500 uppercase tracking-wide">
                                {group.label}
                              </p>
                              {group.tracks.map((trackId) => {
                                const isActive = currentFocusTrack === trackId;
                                const name = TRACK_NAMES[trackId];
                                return (
                                  <button
                                    key={trackId}
                                    onClick={() => handleTrackSelect(trackId)}
                                    disabled={isChangingTrack}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm rounded transition-colors duration-150 disabled:cursor-not-allowed ${
                                      isActive
                                        ? 'bg-indigo-50 text-indigo-700 font-medium'
                                        : 'text-gray-700 hover:bg-white'
                                    }`}
                                  >
                                    <span className="flex-1 truncate">{name}</span>
                                    {isActive && <FiCheck className="text-indigo-500 shrink-0" size={13} />}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
              {canUseEarlyGradeMove(user.email, user.role) && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowTrackPanel(false);
                    navigate('/early-grade-move');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200"
                >
                  <FiUsers className="text-gray-500 shrink-0" />
                  <span>Move Pre-K / K student</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? (
                  <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FiLogOut className="text-gray-500" />
                )}
                <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfileDropdown;
